// HTTP client for the Views surface: /api/views (CONTRACTS §3.5), the Data
// Cloud catalog it builds on, and add_to_workbook for "Put in Excel".
import type { DatasetDef, PeriodBinding, ViewSpec } from './viewModel'

const PRODUCTION_API_ORIGINS: Record<string, string> = { 'app.fusegrid.io': 'https://api.fusegrid.io' }
export const TOKEN_KEY = 'fusegrid_api_token'
const DATA_CLOUD = '/api/platform/v1/data-cloud'

export function apiOrigin(loc: Pick<Location, 'hostname' | 'origin'> = window.location): string {
  return PRODUCTION_API_ORIGINS[String(loc.hostname).toLowerCase()] || loc.origin
}

export type Company = { company_key: string; display_name: string; workbook_id: string; role?: string }
export type Clarification = { pointer: string; question: string; options: Array<{ id: string; label: string }> }
export type ResolvedArgument = { pointer: string; said: string; resolved_id: string; resolved_label: string; confidence: number; resolver: string }
export type DescribeResult = { spec: ViewSpec | null; period_binding: PeriodBinding; resolved: ResolvedArgument[]; summary: string; needs_clarification?: Clarification }
export type PreviewResult = {
  status: string
  data_product_id: string
  row_count: number | null
  columns: Array<{ id: string; label: string }>
  rows: string[][]
  summary: string
}
export type RefreshReceipt = {
  version: number
  data_product_id: string
  source_set_sha256: string
  status: string
  row_count: number | null
  refreshed_at: string | null
}
export type SavedView = {
  view_id: string
  view_key: string
  name: string
  scope: 'personal' | 'workspace'
  spec: ViewSpec
  period_binding: PeriodBinding
  summary: string
  latest_refresh: RefreshReceipt | null
}

export class ViewsApiError extends Error {
  readonly status: number
  readonly code: string | null
  constructor(message: string, status: number, code: string | null) {
    super(message)
    this.status = status
    this.code = code
  }
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

export function createViewsApi({
  origin = apiOrigin(),
  token = () => window.localStorage.getItem(TOKEN_KEY) || '',
  fetchImpl = (input, init) => fetch(input, init),
}: { origin?: string; token?: () => string; fetchImpl?: FetchLike } = {}) {
  async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
    const response = await fetchImpl(`${origin}${path}`, {
      method: init.method || 'GET',
      headers: { Authorization: `Bearer ${token()}`, ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      cache: 'no-store',
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      const code = payload?.error?.code ?? null
      throw new ViewsApiError(payload?.error?.message || code || `FuseGrid request failed (${response.status}).`, response.status, code)
    }
    return payload as T
  }
  const q = encodeURIComponent
  return {
    companies: async () => (await call<{ data: { companies: Company[] } }>(`${DATA_CLOUD}/companies`)).data.companies || [],
    datasets: async (workbookId: string) => (await call<{ data: { datasets: DatasetDef[] } }>(`${DATA_CLOUD}/datasets?workbook_id=${q(workbookId)}`)).data.datasets || [],
    resolveExploreContext: async (exploreToken: string) =>
      (await call<{ data: { context: { company_key: string; semantic_spec: ViewSpec } } }>(`${DATA_CLOUD}/explore-contexts/resolve?token=${q(exploreToken)}`)).data.context,
    describe: (company: string, text: string) => call<DescribeResult>('/api/views/describe', { method: 'POST', body: { company, text } }),
    preview: (company: string, spec: ViewSpec, periodBinding: PeriodBinding) =>
      call<PreviewResult>('/api/views/preview', { method: 'POST', body: { company, spec, period_binding: periodBinding } }),
    save: (company: string, name: string, spec: ViewSpec, scope: 'personal' | 'workspace', periodBinding: PeriodBinding) =>
      call<SavedView>('/api/views', { method: 'POST', body: { company, name, spec, scope, period_binding: periodBinding } }),
    list: async (company: string) => (await call<{ views: SavedView[] }>(`/api/views?company=${q(company)}`)).views || [],
    refresh: (viewId: string, asOf?: string) => call<RefreshReceipt>(`/api/views/${q(viewId)}/refresh`, { method: 'POST', body: asOf ? { as_of: asOf } : {} }),
    members: async (viewId: string, dimension: string, search: string) =>
      (await call<{ members: Array<{ id: string; label: string }> }>(`/api/views/${q(viewId)}/members?dimension=${q(dimension)}&q=${q(search)}`)).members,
    /** Queue place_output kind=view_link through add_to_workbook; the owner approves it in the pane. */
    putInExcel: (company: string, viewId: string, mode: 'table' | 'formulas') =>
      call<{ ok: boolean; operation?: unknown; error?: { code: string; message?: string } }>('/api/capabilities/invoke', {
        method: 'POST',
        body: {
          capability: 'add_to_workbook',
          company,
          request_id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`,
          arguments: { company, output: { kind: 'view_link', view_id: viewId, mode } },
        },
      }),
    packs: async (workbookId: string) => (await call<{ data: { packs: unknown[] } }>(`${DATA_CLOUD}/packs?workbook_id=${q(workbookId)}&host=web`)).data.packs || [],
    packTemplates: async (workbookId: string) => (await call<{ data: { templates: unknown[] } }>(`${DATA_CLOUD}/pack-templates?workbook_id=${q(workbookId)}`)).data.templates || [],
    createPackDraft: (workbookId: string, manifest: unknown) => call<{ data: { pack_template_id: string } }>(`${DATA_CLOUD}/packs`, { method: 'POST', body: { workbook_id: workbookId, manifest } }),
    publishPack: (workbookId: string, templateId: string) => call(`${DATA_CLOUD}/packs/${q(templateId)}/publish`, { method: 'POST', body: { workbook_id: workbookId } }),
    rollbackPack: (workbookId: string, templateId: string) => call(`${DATA_CLOUD}/packs/${q(templateId)}/rollback`, { method: 'POST', body: { workbook_id: workbookId } }),
    retirePack: (workbookId: string, templateId: string) => call(`${DATA_CLOUD}/packs/${q(templateId)}/retire`, { method: 'POST', body: { workbook_id: workbookId } }),
  }
}

export type ViewsApi = ReturnType<typeof createViewsApi>

/** Exchange a shared-demo drill-through token for isolated browser tokens; null for ordinary links. */
export async function startDemoExploreSession({ origin, token, fetchImpl = (i, init) => fetch(i, init) }: { origin: string; token: string; fetchImpl?: FetchLike }) {
  const response = await fetchImpl(`${origin}/api/auth/demo-explore-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
    cache: 'no-store',
  })
  if (response.status === 404) return null
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload.access_token || !payload.refresh_token) {
    throw new Error(String(payload.error || payload.message || 'The shared demo drill-through session is not available right now.'))
  }
  return { accessToken: String(payload.access_token), refreshToken: String(payload.refresh_token) }
}
